import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdInput, CmdLabel, CmdLoader, CmdSimpleSelect } from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IAddWaypointRequestData,
  IFormWaypointResponseData,
  IWaypoint,
  IWaypointPostion,
} from './types';
import showMessage from '../../Toastr/ToastService';
import PermissionsService, {
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import ConfirmDialog from '../../ConfirmDialog';
import {
  convertCoordinatesToArray,
  createWaypoint,
  filterBinary,
  getWaypointFormData,
  getWaypointList,
} from './WaypointService';
import { ISimpleSelectOption } from '../../Forms/ArrayFields/interfaces';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import SimpleSelectWithIcon from '../../Forms/ReactHookFormFields/SimpleSelectWithIcon';
import { WAYPOINT_QUERY_KEY } from './constants';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  activeFilterAtom,
  centerWaypointAtom,
  computedAreaAtom,
  coordinatesAtom,
  filteredWaypointsAtom,
  filteredWaypointsStringAtom,
  isDrawWaypointAtom,
  waypointIdAtom,
  waypointsAtom,
} from './WaypointState';
import { STATUS_WARNING } from '../../../Services/HttpService';

interface IAddWaypointForm {
  closeAddWaypoint: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  saveAddWaypoint: () => void;
}

interface AddWaypointFormValues {
  icon: string;
  name: string;
  type: string;
  customer: string;
}

export default function AddWaypointForm(props: IAddWaypointForm): JSX.Element {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { formatMessage: f } = useIntl();
  const [waypoints, setWaypoints] = useRecoilState(waypointsAtom);
  const filteredWaypointsString = useRecoilValue<string>(filteredWaypointsStringAtom);
  const setFilteredWaypoints = useSetRecoilState<IWaypoint[]>(filteredWaypointsAtom);
  const computedArea = useRecoilState<number>(computedAreaAtom)[0];
  const activeFilter = useRecoilValue(activeFilterAtom);
  const [formOptions, setFormOptions] = React.useState<IFormWaypointResponseData | null>(null);
  const [customerFormData, setCustomerFormData] = React.useState<ISimpleSelectOption[] | null>(
    null
  );
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = React.useState(false);
  const coordinates = useRecoilValue<IWaypointPostion[] | null>(coordinatesAtom);
  const center = useRecoilValue<IWaypointPostion | null>(centerWaypointAtom);
  const setIsDrawWaypoint = useSetRecoilState<boolean>(isDrawWaypointAtom);
  const setWaypointId = useSetRecoilState<number | null>(waypointIdAtom);
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    setError,
    formState: { errors },
  } = useForm<AddWaypointFormValues>({
    mode: 'onChange',
    defaultValues: {
      icon: '',
      name: '',
      type: '',
      customer:
        selectedCustomers.length === 1 && customerFormData ? String(selectedCustomers[0]) : '',
    },
    resolver: async (data) => {
      const formValidation = {
        customer: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        icon: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        name: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        type: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };

      const validateErros = validateFields(data, formValidation);

      let formErrors = transformErrors(validateErros);

      if (data.name && data.name.trim().length <= 4) {
        formErrors = {
          ...formErrors,
          name: { type: 'required', message: f({ id: 'waypoints.edit.title.required.min.char' }) },
        };
      }
      if (
        data.customer &&
        data.type === 'private' &&
        !PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_PRIVATE,
          ['create'],
          Number(data.customer)
        )
      ) {
        formErrors = {
          ...formErrors,
          type: { type: 'required', message: f({ id: 'waypoints.edit.title.required' }) },
        };
      }
      if (
        data.customer &&
        data.type === 'customer' &&
        !PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_CUSTOMER,
          ['create'],
          Number(data.customer)
        )
      ) {
        formErrors = {
          ...formErrors,
          type: { type: 'required', message: f({ id: 'waypoints.edit.title.required' }) },
        };
      }

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const updateWaypoints = () => {
    queryClient.invalidateQueries({
      queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers, activeFilter],
    });
  };

  const handleFormOptions = async () => {
    const response = await getWaypointFormData();
    setCustomerFormData(null);

    setFormOptions(null);

    if (response) {
      const formDataCustomer: ISimpleSelectOption[] = response.customerId.values.filter(
        (item) =>
          selectedCustomers.includes(Number(item.value)) &&
          (PermissionsService.hasAccess(
            PERMISSION_WAYPOINT_CUSTOMER,
            ['create'],
            Number(item.value)
          ) ||
            PermissionsService.hasAccess(
              PERMISSION_WAYPOINT_PRIVATE,
              ['create'],
              Number(item.value)
            ))
      );
      if (selectedCustomers.length === 1) {
        setValue('customer', String(selectedCustomers[0]));
      }

      setCustomerFormData(formDataCustomer);

      if (response.type) {
        const formDataTooltip = response.type.values.map((item) => {
          if (item.value === 'customer') {
            return { ...item, tooltip: f({ id: 'waypoints.form.customer.type.tooltip' }) };
          }
          return { ...item, tooltip: f({ id: 'waypoints.form.private.type.tooltip' }) };
        });

        setFormOptions({ ...response, type: { values: formDataTooltip } });
      } else {
        setFormOptions(response);
      }
    }
  };

  const { isLoading } = useQuery({
    queryKey: ['addWaypointFormOptions', selectedCustomers],
    queryFn: () => handleFormOptions(),
    // staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleOnSubmit = async (values: AddWaypointFormValues) => {
    const trimName = values.name.trim();

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (coordinates && center) {
      updateWaypoints();

      const data: IAddWaypointRequestData = {
        name: trimName,
        type: values.type,
        centerPoint: center,
        customerId: Number(values.customer),
        area: computedArea,
        categoryId: Number(values.icon),
        coordinates: convertCoordinatesToArray(coordinates, true),
      };

      const newWaypoint = await createWaypoint(data);

      if (newWaypoint && newWaypoint?.coordinates) {
        const waypointsList = await getWaypointList(
          activeFilter ? { filters: { ...activeFilter } } : null
        );
        if (waypointsList) {
          setIsDrawWaypoint(false);

          setWaypoints(waypointsList.waypoints);
          setFilteredWaypoints(filterBinary(filteredWaypointsString, waypoints));
          setWaypointId(Number(newWaypoint.id));
          props.saveAddWaypoint();
        }
      }
      if (newWaypoint && newWaypoint.status === STATUS_WARNING) {
        const message = newWaypoint?.toast?.message;

        if (message) {
          setError('name', {
            type: 'required',
            message: f({ id: `${message}` }),
          });
        }
      }
    } else {
      showMessage(
        f({ id: 'waypoints.draw.warning.message.title' }),
        f({ id: 'waypoints.draw.warning.message' }, { name: trimName }),
        'warning'
      );
    }
  };

  const handleConfirmDialog = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setIsOpenConfirmDialog(true);
  };
  const handleContinueConfirmDialog = () => {
    setIsOpenConfirmDialog(false);

    props.closeAddWaypoint();
  };

  const handleCloseConfirmDialog = () => {
    setIsOpenConfirmDialog(false);
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      {isLoading && <CmdLoader inContent={true} />}
      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-title">
          <a href="#">
            <i className="cmd-icon cmd-icon-back mr-1" onClick={handleConfirmDialog} />
            {f({ id: 'waypoints.newWaypoint' })}
          </a>
        </div>
        <div className="b-mc-waypoints-content">
          {!formOptions && <CmdLoader inContent={true} />}
          {isOpenConfirmDialog && (
            <ConfirmDialog
              title={`${f({
                id: 'waypoint.add.confirm.dialog.title',
              })}`}
              cancelCallback={handleCloseConfirmDialog}
              continueCallback={handleContinueConfirmDialog}
              cancelButtonTitle={f({
                id: 'waypoint.add.confirm.dialog.cancel',
              })}
              continueButtonTitle={f({
                id: 'waypoint.add.confirm.dialog.confirm',
              })}
            >
              {f({ id: 'waypoint.add.confirm.dialog.message' })}
            </ConfirmDialog>
          )}

          {selectedCustomers.length > 1 && customerFormData && (
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithSearchHookForm
                  {...field}
                  id="customer"
                  // name="customer"
                  label={f({ id: 'waypoints.form.customer.title' })}
                  // labelClassName="col-3 form-label"
                  introOption={f({ id: 'form.choose' })}
                  options={customerFormData}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                  control={control}
                  required={true}
                />
              )}
            />
          )}
          {selectedCustomers.length === 1 && customerFormData && (
            <CmdLabel
              id="customer"
              name="customer"
              title={f({ id: 'waypoints.form.customer.title' })}
              value={`${customerFormData[0].item} (${customerFormData[0].label})`}
            />
          )}
          {formOptions && formOptions.categoryId && (
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithIcon
                  {...field}
                  // name="icon"
                  // id="icon"
                  label={f({ id: 'waypoints.form.category.title' })}
                  options={formOptions.categoryId.values}
                  introOption={f({ id: 'form.choose' })}
                  control={control}
                  required={true}
                />
              )}
            />
          )}
          {formOptions && (
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  // name="name"
                  id="name"
                  type="text"
                  // labelClassName="col-3 form-label"
                  label={f({ id: 'waypoints.edit.title' })}
                  // newStyle={true}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  placeholder={f({ id: 'waypoints.edit.title.required.placeholder' })}
                  onChangeCallback={(value) => {
                    setValue('name', value);
                    trigger('name');
                  }}
                  required={true}
                  error={errors.name?.message}
                  touched={!!errors.name}
                />
              )}
            />
          )}

          {formOptions && formOptions.type && (
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="type"
                  // name="type"
                  label={f({ id: 'waypoints.edit.type' })}
                  // labelClassName="col-3 form-label"
                  introOption={
                    !watch('customer')
                      ? f({ id: 'waypoints.form.customer.pick' })
                      : f({ id: 'form.choose' })
                  }
                  options={[
                    ...formOptions.type.values.filter(
                      (item) =>
                        item.value === 'customer' &&
                        PermissionsService.hasAccess(
                          PERMISSION_WAYPOINT_CUSTOMER,
                          ['create'],
                          Number(watch('customer'))
                        )
                    ),
                    ...formOptions.type.values.filter(
                      (item) =>
                        item.value === 'private' &&
                        PermissionsService.hasAccess(
                          PERMISSION_WAYPOINT_PRIVATE,
                          ['create'],
                          Number(watch('customer'))
                        )
                    ),
                  ]}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  disabled={!watch('customer')}
                  onChangeCallback={(value) => {
                    setValue('type', value);
                    trigger('type');
                  }}
                  required={true}
                  error={errors.type?.message}
                  touched={true}
                />
              )}
            />
          )}

          {/* <div className="form-group">
                  <div className="row align-items-center">
                    <label htmlFor="" className="col-3 form-label">
                      Zakaznik
                    </label>
                    <div className="col-9">SimpleSelect with search</div>
                  </div>
                </div> */}
        </div>
        <div className="b-mc-waypoints-footer">
          <div className="row">
            <div className="col">
              <button className="e-button" onClick={handleConfirmDialog}>
                {f({ id: 'waypoints.cancel' })}
              </button>
            </div>
            <div className="col">
              <button
                type="submit"
                className={`${Object.keys(errors).length > 0 ? 'e-button e-button--gray  e-button--disabled' : 'e-button e-button--gray'}`}
                disabled={Object.keys(errors).length > 0}
              >
                {f({ id: 'waypoints.edit.save' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
